export const CAMPAIGN_NAME_MAX_LENGTH = 200;
export const RULES_OR_CONDITIONS_MAX_LENGTH = 4000;
export const SAO_PAULO_TIME_ZONE = 'America/Sao_Paulo';
export const CAMPAIGN_NAME_TOO_LONG_MESSAGE =
  'Título da campanha excede 200 caracteres';
export const RULES_TOO_LONG_MESSAGE =
  'Regras ou condições excede 4000 caracteres';
export const INVALID_DATE_RANGE_MESSAGE = 'Intervalo de datas inválido';
export const EMPTY_RULE_HISTORY_MESSAGE =
  'Sem histórico desde o deploy; regra atual em GET /coupon/id';

const EMPTY_VALUE = '(vazio)';

const FIELD_LABELS: Record<string, string> = {
  acquisition: 'Aquisição',
  campaignName: 'Título da campanha',
  couponType: 'Tipo do cupom',
  dateExpired: 'Data de expiração',
  enabled: 'Ativo',
  blogger: 'Blogger',
  couponUsageTypeId: 'Categoria',
  freeShipping: 'Frete grátis',
  freebieCount: 'Quantidade de brindes',
  freebieMessage: 'Mensagem do cupom',
  from: 'De',
  glampartner: 'Glampartner',
  glampoints: 'Glampoints',
  influencerStatusTypeIdRestriction: 'Restrição Bflu',
  maxRedeems: 'Máximo de uso',
  mgm: 'MGM',
  name: 'Nome do cupom',
  recurringCycles: 'Meses com desconto',
  renewal: 'Renovação',
  rulesOrConditions: 'Regras ou condições',
  to: 'Até',
  upgrade: 'Upgrade',
  value: 'Valor'
};

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Criação',
  CREATED: 'Criação',
  INSERT: 'Criação',
  UPDATE: 'Alteração',
  UPDATED: 'Alteração',
  DELETE: 'Exclusão',
  DELETED: 'Exclusão'
};

export interface CouponTextFieldState {
  campaignName: string | null;
  hasCampaignName: boolean;
  hasRulesOrConditions: boolean;
  rulesOrConditions: string | null;
}

export interface CouponRuleHistoryEntry {
  action?: string | null;
  actor?: string | null;
  after?: Record<string, unknown> | null;
  before?: Record<string, unknown> | null;
  changedFields?: string[] | null;
  couponId?: number | null;
  couponRuleLogId?: number | null;
  dateCreated?: string | null;
  userId?: number | null;
}

export interface CouponRuleDiffLine {
  afterText: string;
  beforeText: string;
  fieldLabel: string;
}

export interface CouponRuleHistoryView {
  actionLabel: string;
  actor: string;
  changedFieldsLabel: string;
  diffLines: CouponRuleDiffLine[];
  hasDiff: boolean;
  trackId: string;
  whenLabel: string;
}

export interface CouponRuleHistoryQuery {
  error?: string;
  from?: string;
  to?: string;
}

export function blankToNull(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  return value.trim().length === 0 ? null : value;
}

export function readCouponTextFields(
  model: object | null | undefined
): CouponTextFieldState {
  const record = asRecord(model);
  return {
    campaignName: readText(record, 'campaignName'),
    hasCampaignName: record != null && 'campaignName' in record,
    hasRulesOrConditions: record != null && 'rulesOrConditions' in record,
    rulesOrConditions: readText(record, 'rulesOrConditions')
  };
}

export function withReplacedCouponTextFields<T extends object>(
  request: T,
  fields: {
    campaignName: string | null | undefined;
    rulesOrConditions: string | null | undefined;
  }
): T & { campaignName: string | null; rulesOrConditions: string | null } {
  return {
    ...request,
    campaignName: blankToNull(fields.campaignName),
    rulesOrConditions: blankToNull(fields.rulesOrConditions)
  };
}

export function couponRuleHistoryParams(range: {
  from?: Date | null;
  to?: Date | null;
}): CouponRuleHistoryQuery {
  const from = toIsoOrUndefined(range.from);
  const to = toIsoOrUndefined(range.to);
  if (from && to && from > to) {
    return { error: INVALID_DATE_RANGE_MESSAGE };
  }
  const query: CouponRuleHistoryQuery = {};
  if (from) {
    query.from = from;
  }
  if (to) {
    query.to = to;
  }
  return query;
}

function toIsoOrUndefined(date: Date | null | undefined): string | undefined {
  if (!date || Number.isNaN(date.getTime())) {
    return undefined;
  }
  return saoPauloWallTimeToIso(date);
}

export function saoPauloWallTimeToIso(date: Date): string {
  return zonedWallTimeToUtc(date, SAO_PAULO_TIME_ZONE).toISOString();
}

export function formatSaoPauloDateTime(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  const date =
    value instanceof Date
      ? value
      : new Date(
          typeof value === 'number' || typeof value === 'string' ? value : ''
        );
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: SAO_PAULO_TIME_ZONE,
    year: 'numeric'
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('day')}/${part('month')}/${part('year')} ${part('hour')}:${part('minute')}:${part('second')}`;
}

export function unwrapRuleHistory(body: unknown): CouponRuleHistoryEntry[] {
  const record = asRecord(body);
  const payload = record && 'result' in record ? record['result'] : body;
  if (payload == null) {
    return [];
  }
  if (!Array.isArray(payload)) {
    throw new Error(
      'A API de histórico de regras devolveu um formato inesperado.'
    );
  }
  return payload.map((item, index) => normalizeEntry(item, index));
}

export function ruleInEffect<T>(
  entries: readonly T[] | null | undefined
): T | null {
  if (!entries || entries.length === 0) {
    return null;
  }
  return entries[0];
}

export function toCouponRuleHistoryView(
  entry: CouponRuleHistoryEntry,
  index: number
): CouponRuleHistoryView {
  const diffLines = buildDiffLines(entry);
  return {
    actionLabel: actionLabel(entry.action),
    actor: entry.actor?.trim() ? entry.actor.trim() : 'Não informado',
    changedFieldsLabel:
      entry.changedFields && entry.changedFields.length > 0
        ? entry.changedFields.map((field) => fieldLabel(field)).join(', ')
        : 'Nenhum',
    diffLines,
    hasDiff: diffLines.length > 0,
    trackId: String(entry.couponRuleLogId ?? index),
    whenLabel: formatSaoPauloDateTime(entry.dateCreated) || 'Data não informada'
  };
}

export function formatCouponValidationError(error: unknown): string {
  const status = readStatus(error);
  const body = readBody(error);
  const message = readMessage(body);
  if (status === 400 && message) {
    return message;
  }
  const fieldErrors = collectFieldErrors(body);
  if (fieldErrors.length > 0) {
    return fieldErrors.join('\n');
  }
  const fallback = message || readMessage(error);
  if (fallback) {
    return translateFieldNames(fallback);
  }
  if (status === 404) {
    return 'Histórico de regras indisponível neste ambiente (404).';
  }
  if (status === 400) {
    return 'Os dados enviados não foram aceitos.';
  }
  return 'Erro ao realizar esta operação, entre em contato com o suporte';
}

export function couponDialogError(
  error: unknown,
  message: string
): { error: { message: string }; message: string; status?: number } {
  return {
    error: { message },
    message,
    status: readStatus(error)
  };
}

export function isHttpStatus(error: unknown, status: number): boolean {
  return readStatus(error) === status;
}

function buildDiffLines(entry: CouponRuleHistoryEntry): CouponRuleDiffLine[] {
  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const fields =
    entry.changedFields && entry.changedFields.length > 0
      ? entry.changedFields
      : differingKeys(before, after);
  return fields.map((field) => ({
    afterText: formatRuleValue(after[field]),
    beforeText: formatRuleValue(before[field]),
    fieldLabel: fieldLabel(field)
  }));
}

function differingKeys(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): string[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((key) => !sameValue(before[key], after[key]));
}

function sameValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export function formatRuleValue(value: unknown): string {
  if (value == null) {
    return EMPTY_VALUE;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0 ? EMPTY_VALUE : value;
  }
  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function normalizeEntry(item: unknown, index: number): CouponRuleHistoryEntry {
  const record = asRecord(item) ?? {};
  const actor =
    readText(record, 'actor') ??
    readText(record, 'userName') ??
    readText(record, 'userEmail');
  const dateCreated =
    readText(record, 'dateCreated') ??
    readText(record, 'occurredAt') ??
    readText(record, 'createdAt');
  const couponRuleLogId =
    typeof record['couponRuleLogId'] === 'number'
      ? record['couponRuleLogId']
      : typeof record['couponRuleHistoryId'] === 'number'
        ? record['couponRuleHistoryId']
        : index;
  return {
    action: readText(record, 'action'),
    actor,
    after: readSnapshot(record['after']),
    before: readSnapshot(record['before']),
    changedFields: readChangedFields(record['changedFields']),
    couponId:
      typeof record['couponId'] === 'number' ? record['couponId'] : null,
    couponRuleLogId,
    dateCreated,
    userId: typeof record['userId'] === 'number' ? record['userId'] : null
  };
}

function readSnapshot(value: unknown): Record<string, unknown> | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return readSnapshot(JSON.parse(trimmed));
    } catch {
      return { value };
    }
  }
  return asRecord(value);
}

function readChangedFields(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === 'string' && item.length > 0
    );
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  return [];
}

function actionLabel(action: string | null | undefined): string {
  if (!action || action.trim().length === 0) {
    return 'Não informada';
  }
  return ACTION_LABELS[action.trim().toUpperCase()] ?? action.trim();
}

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

function translateFieldNames(message: string): string {
  return message
    .replaceAll('rulesOrConditions', 'Regras ou condições')
    .replaceAll('campaignName', 'Título da campanha');
}

function collectFieldErrors(body: unknown): string[] {
  const record = asRecord(body);
  if (!record) {
    return [];
  }
  const collections = [
    record['errors'],
    record['fieldErrors'],
    record['violations']
  ];
  const lines: string[] = [];
  collections.forEach((collection) => {
    if (!Array.isArray(collection)) {
      return;
    }
    collection.forEach((item) => {
      const line = fieldErrorLine(item);
      if (line) {
        lines.push(line);
      }
    });
  });
  return lines;
}

function fieldErrorLine(item: unknown): string | null {
  if (typeof item === 'string' && item.trim().length > 0) {
    return translateFieldNames(item.trim());
  }
  const record = asRecord(item);
  if (!record) {
    return null;
  }
  const field =
    readText(record, 'field') ??
    readText(record, 'property') ??
    readText(record, 'fieldName');
  const message =
    readText(record, 'message') ??
    readText(record, 'defaultMessage') ??
    readText(record, 'reason');
  if (!message) {
    return null;
  }
  return field
    ? `${fieldLabel(field)}: ${message}`
    : translateFieldNames(message);
}

function readMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const message = record['message'];
  if (typeof message === 'string' && message.trim().length > 0) {
    return message.trim();
  }
  if (Array.isArray(message)) {
    const lines = message.filter(
      (item): item is string => typeof item === 'string'
    );
    return lines.length > 0 ? lines.join('\n') : null;
  }
  return null;
}

function readBody(error: unknown): unknown {
  const record = asRecord(error);
  if (!record || !('error' in record)) {
    return error;
  }
  return record['error'];
}

function readStatus(error: unknown): number | undefined {
  const record = asRecord(error);
  return typeof record?.['status'] === 'number' ? record['status'] : undefined;
}

function readText(
  record: Record<string, unknown> | null,
  key: string
): string | null {
  if (!record) {
    return null;
  }
  const value = record[key];
  if (typeof value === 'string') {
    return value;
  }
  if (
    typeof value === 'number' &&
    key !== 'campaignName' &&
    key !== 'rulesOrConditions'
  ) {
    return String(value);
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function zonedWallTimeToUtc(date: Date, timeZone: string): Date {
  if (Number.isNaN(date.getTime())) {
    return new Date(NaN);
  }
  const utcGuess = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
  const offset = timeZoneOffsetMs(new Date(utcGuess), timeZone);
  const adjusted = utcGuess - offset;
  const offsetAtAdjusted = timeZoneOffsetMs(new Date(adjusted), timeZone);
  return new Date(utcGuess - offsetAtAdjusted);
}

function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric'
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((item) => item.type === type)?.value);
  const asUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second')
  );
  return asUtc - instant.getTime();
}
